import { createContext, useContext, useEffect, useState } from 'react';

const translations = {
  pt: {
    nav: {
      home: 'Início',
      about: 'Sobre',
      services: 'Serviços',
      process: 'Processo',
      faq: 'FAQ',
      contact: 'Contato',
      cta: 'Fale conosco',
    },
    home: {
      overline: 'Software sob medida',
      titleLines: ['Sites, sistemas', 'e automação', 'industrial.'],
      subtitle:
        'Fazemos o site da sua empresa e automatizamos o que hoje é feito na mão.',
      ctaPrimary: 'Falar com a gente',
      ctaSecondary: 'Ver serviços',
      pillars: {
        overline: 'O que fazemos',
        items: [
          {
            title: 'Automação industrial',
            body: 'Para a fábrica que ainda faz na mão o que o software faria sozinho.',
            list: 'Estoque, relatórios, apontamento de produção, integração com ERP e alertas.',
          },
          {
            title: 'Sites e sistemas web',
            body: 'Para empresa de qualquer segmento que precisa aparecer e atender melhor.',
            list: 'Site institucional, loja virtual, landing page e sistema com login.',
          },
        ],
        link: 'Ver todos os serviços',
      },
      cta: {
        title: 'Vamos olhar o seu caso?',
        body: 'Conte o que você precisa e receba uma análise inicial, sem compromisso.',
        button: 'Fale conosco',
      },
    },
    about: {
      overline: 'Sobre a JN',
      title: 'Software é a única coisa que a gente faz.',
      intro:
        'Empresa 100% remota. Do site ao chão de fábrica, sem promessas infladas.',
      mission: {
        title: 'Missão',
        body: 'Transformar trabalho manual em software confiável e mensurável.',
      },
      vision: {
        title: 'Visão',
        body: 'Ser a escolha óbvia de quem precisa de software bem feito.',
      },
      valuesOverline: 'Valores',
      valuesTitle: 'Como a gente trabalha',
      values: [
        { n: '01', title: 'Precisão técnica', desc: 'Tudo documentado. Se um dia você trocar de fornecedor, o próximo entende o que foi feito.' },
        { n: '02', title: 'Transparência', desc: 'Escopo, prazo e preço por escrito antes de começar.' },
        { n: '03', title: 'Segurança', desc: 'Backup desde o primeiro dia.' },
        { n: '04', title: 'Melhoria contínua', desc: 'Cada projeto melhora o próximo.' },
        { n: '05', title: 'Parceria', desc: 'A gente continua respondendo depois da entrega.' },
      ],
    },
    services: {
      overline: 'Serviços',
      title: 'Duas frentes, o mesmo cuidado técnico',
      intro: 'Automação para a fábrica. Sites e sistemas para o resto.',
      groups: [
        {
          title: 'Automação industrial',
          note: 'Para indústrias com processo manual no meio do caminho.',
          items: [
            { n: '01', title: 'Controle de estoque', desc: 'Entradas, saídas e alertas de reposição em tempo real.' },
            { n: '02', title: 'Automação de tarefas', desc: 'Rotinas que acabam com a digitação repetitiva.' },
            { n: '03', title: 'Apontamento de produção', desc: 'Registro digital de produção, paradas e refugo.' },
            { n: '04', title: 'Dashboards e indicadores', desc: 'KPIs em tempo real, prontos para decidir.' },
            { n: '05', title: 'Relatórios automáticos', desc: 'PDF e Excel gerados e enviados sozinhos.' },
            { n: '06', title: 'Integração entre sistemas', desc: 'ERP, planilhas e bancos de dados conversando.' },
            { n: '07', title: 'Rastreabilidade', desc: 'Histórico de lotes e movimentações em segundos.' },
            { n: '08', title: 'Alertas automáticos', desc: 'Avisos por WhatsApp ou e-mail fora do padrão.' },
            { n: '09', title: 'Processos digitais', desc: 'Formulários e aprovações sem papel.' },
          ],
        },
        {
          title: 'Sites e sistemas web',
          note: 'Para empresas de qualquer porte e segmento — não só indústria.',
          items: [
            { n: '10', title: 'Site institucional', desc: 'A cara da empresa, rápida e encontrável no Google.' },
            { n: '11', title: 'Loja virtual', desc: 'Catálogo, carrinho e pagamento funcionando.' },
            { n: '12', title: 'Landing page', desc: 'Página única de campanha, feita para captar contato.' },
            { n: '13', title: 'Sistema web com login', desc: 'Área restrita onde cada cliente vê o que é dele.' },
          ],
        },
      ],
      change: {
        overline: 'Na prática',
        title: 'O que muda',
        todayLabel: 'Hoje',
        afterLabel: 'Depois',
        pairs: [
          {
            group: 'Automação',
            today: 'O estoque vive numa planilha que alguém atualiza à mão.',
            after: 'Saldo em tempo real, com aviso quando chega no mínimo.',
          },
          {
            today: 'Toda segunda alguém monta o relatório copiando e colando.',
            after: 'Gerado e enviado sozinho.',
          },
          {
            today: 'O problema só aparece no fim do turno.',
            after: 'WhatsApp na hora em que sai do padrão.',
          },
          {
            group: 'Sites e sistemas',
            today: 'O site da empresa é o perfil do Instagram.',
            after: 'Site próprio, que aparece no Google.',
          },
          {
            today: 'Mudar um preço no site depende de chamar alguém.',
            after: 'Você mesmo altera, em dois minutos.',
          },
          {
            today: 'O cliente pede tudo por WhatsApp e alguém anota.',
            after: 'Portal com login onde ele mesmo acompanha.',
          },
        ],
      },
      industriesOverline: 'Segmentos',
      industriesTitle: 'Setores para os quais projetamos automação',
      industries: [
        'Alimentos e bebidas', 'Automotivo', 'Farmacêutico', 'Químico', 'Metalurgia',
        'Plásticos', 'Embalagens', 'Papel e celulose', 'Logística', 'Saneamento e utilities',
      ],
      industriesNote: 'Site e sistema web a gente faz para empresa de qualquer porte e segmento.',
      cta: {
        title: 'Precisa de algum desses?',
        body: 'Conte o seu caso e receba uma proposta por escrito.',
        button: 'Falar com a gente',
      },
    },
    // ATENCAO: os precos abaixo sao PROVISORIOS (colocados como exemplo).
    // Trocar pelos valores reais antes de qualquer publicacao.

    process: {
      overline: 'Processo',
      title: 'Como um projeto anda',
      intro: 'Do primeiro contato ao suporte.',
      steps: [
        { n: '01', title: 'Diagnóstico', desc: 'Entendemos o que você faz hoje e onde dói.' },
        { n: '02', title: 'Proposta', desc: 'Escopo, prazo e preço por escrito.' },
        { n: '03', title: 'Projeto', desc: 'Arquitetura, dados e protótipo das telas.' },
        { n: '04', title: 'Desenvolvimento', desc: 'Entregas parciais — você acompanha o andamento.' },
        { n: '05', title: 'Validação', desc: 'Testes com dados reais antes de entrar no ar.' },
        { n: '06', title: 'Treinamento', desc: 'A gente ensina quem vai usar.' },
        { n: '07', title: 'Suporte', desc: 'Ajustes finos e atendimento remoto.' },
      ],
    },
    faq: {
      overline: 'FAQ',
      title: 'Perguntas frequentes',
      items: [
        { q: 'Quanto custa um projeto?', a: 'Depende do escopo. Você recebe uma proposta por escrito, sem compromisso.' },
        { q: 'Qual é o prazo médio?', a: 'De 2 a 6 semanas nos projetos simples. Os maiores são divididos em fases, com entregas parciais.' },
        { q: 'Vocês atendem fora de São Paulo?', a: 'Sim — por ser um trabalho de software, atendemos todo o Brasil de forma 100% remota.' },
        { q: 'Vocês fazem site também?', a: 'Fazemos: site institucional, loja virtual, landing page e sistema web com login. Essa parte não é só para indústria — atendemos empresa de qualquer porte e segmento.' },
        { q: 'Vocês fazem apenas a parte de software?', a: 'Sim — programamos sites, sistemas e automações; não executamos instalações elétricas nem retrofit de máquinas.' },
        { q: 'Vocês assumem sistemas feitos por terceiros?', a: 'Sim. Assumimos, documentamos e melhoramos o que já existe.' },
        { q: 'Como começo?', a: 'Pelo formulário ou pelo WhatsApp. Em seguida marcamos uma conversa.' },
      ],
    },
    contact: {
      overline: 'Contato',
      title: 'Conte o que você precisa',
      intro: 'Respondemos com uma análise inicial, sem compromisso.',
      form: {
        name: 'Nome',
        namePh: 'Seu nome completo',
        email: 'E-mail',
        emailPh: 'voce@empresa.com.br',
        whatsapp: 'WhatsApp',
        whatsappPh: '(11) 90000-0000',
        company: 'Empresa (opcional)',
        companyPh: 'Nome da empresa',
        message: 'O que você precisa',
        messagePh: 'Ex.: estoque numa planilha, relatório montado à mão toda semana, ou um site que a empresa ainda não tem…',
        submit: 'Enviar',
        sending: 'Enviando…',
        successTitle: 'Recebido.',
        successMsg: 'Retornamos pelo WhatsApp ou e-mail que você informou.',
        errorMsg: 'Não foi possível enviar. Tente de novo ou chame no WhatsApp.',
        required: 'Preencha os campos obrigatórios.',
      },
      channelsTitle: 'Canais diretos',
      whatsappLabel: 'WhatsApp',
      whatsappValue: '(11) 99684-5494',
      emailLabel: 'E-mail',
      emailValue: 'jnautomacoes@gmail.com',
      locationLabel: 'Atendimento',
      locationValue: 'Home-office — 100% remoto, em todo o Brasil',
      soon: 'Instagram em breve.',
    },
    footer: {
      tagline: 'Sites, sistemas e automação com precisão de engenharia.',
      navTitle: 'Navegação',
      contactTitle: 'Contato',
      rights: '© 2026 JN Automações. Todos os direitos reservados.',
    },
    notFound: {
      code: '404',
      title: 'Página não encontrada.',
      msg: 'Esse endereço não existe aqui.',
      back: 'Voltar ao início',
    },
    whatsappMessage: 'Olá! Gostaria de falar sobre um projeto.',
  },
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      services: 'Services',
      process: 'Process',
      faq: 'FAQ',
      contact: 'Contact',
      cta: 'Talk to us',
    },
    home: {
      overline: 'Custom software',
      titleLines: ['Websites, systems', 'and industrial', 'automation.'],
      subtitle:
        'We build your website and automate what is still done by hand.',
      ctaPrimary: 'Talk to us',
      ctaSecondary: 'See services',
      pillars: {
        overline: 'What we do',
        items: [
          {
            title: 'Industrial automation',
            body: 'For the factory still doing by hand what software would do on its own.',
            list: 'Inventory, reports, production logging, ERP integration and alerts.',
          },
          {
            title: 'Websites and web systems',
            body: 'For any company that needs to be found and to serve people better.',
            list: 'Company website, online store, landing page and system with login.',
          },
        ],
        link: 'See all services',
      },
      cta: {
        title: 'Shall we look at your case?',
        body: 'Tell us what you need and get an initial analysis, no strings attached.',
        button: 'Talk to us',
      },
    },
    about: {
      overline: 'About JN',
      title: 'Software is the only thing we do.',
      intro:
        'A fully remote company. From your website to the factory floor, no inflated promises.',
      mission: {
        title: 'Mission',
        body: 'To turn manual work into reliable, measurable software.',
      },
      vision: {
        title: 'Vision',
        body: 'To be the obvious choice for well-built software.',
      },
      valuesOverline: 'Values',
      valuesTitle: 'How we work',
      values: [
        { n: '01', title: 'Technical precision', desc: 'Everything documented. If you ever change supplier, the next one understands what was built.' },
        { n: '02', title: 'Transparency', desc: 'Scope, timeline and price in writing before we start.' },
        { n: '03', title: 'Safety', desc: 'Backups from day one.' },
        { n: '04', title: 'Continuous improvement', desc: 'Every project improves the next.' },
        { n: '05', title: 'Partnership', desc: 'We keep answering after delivery.' },
      ],
    },
    services: {
      overline: 'Services',
      title: 'Two fronts, the same technical care',
      intro: 'Automation for the factory. Websites and systems for everyone else.',
      groups: [
        {
          title: 'Industrial automation',
          note: 'For factories with a manual step in the middle of the process.',
          items: [
            { n: '01', title: 'Inventory control', desc: 'Inbound, outbound and restocking alerts in real time.' },
            { n: '02', title: 'Task automation', desc: 'Routines that end repetitive typing.' },
            { n: '03', title: 'Production logging', desc: 'Digital records of output, stoppages and scrap.' },
            { n: '04', title: 'Dashboards and indicators', desc: 'Real-time KPIs, ready for decisions.' },
            { n: '05', title: 'Automatic reports', desc: 'PDF and Excel generated and sent by themselves.' },
            { n: '06', title: 'Systems integration', desc: 'ERP, spreadsheets and databases talking.' },
            { n: '07', title: 'Traceability', desc: 'Batch and movement history in seconds.' },
            { n: '08', title: 'Automatic alerts', desc: 'WhatsApp or email warnings off-standard.' },
            { n: '09', title: 'Digital processes', desc: 'Forms and approvals without paper.' },
          ],
        },
        {
          title: 'Websites and web systems',
          note: 'For companies of any size and sector — not only industry.',
          items: [
            { n: '10', title: 'Company website', desc: 'The face of the business, fast and findable on Google.' },
            { n: '11', title: 'Online store', desc: 'Catalogue, cart and payment working.' },
            { n: '12', title: 'Landing page', desc: 'A single campaign page, built to capture contacts.' },
            { n: '13', title: 'Web system with login', desc: 'A restricted area where each client sees their own data.' },
          ],
        },
      ],
      change: {
        overline: 'In practice',
        title: 'What changes',
        todayLabel: 'Today',
        afterLabel: 'After',
        pairs: [
          {
            group: 'Automation',
            today: 'Inventory lives in a spreadsheet someone updates by hand.',
            after: 'Balance in real time, with a warning when it hits the minimum.',
          },
          {
            today: 'Every Monday someone builds the report by copying and pasting.',
            after: 'Generated and sent on its own.',
          },
          {
            today: 'The problem only shows up at the end of the shift.',
            after: 'A WhatsApp message the moment it goes off-standard.',
          },
          {
            group: 'Websites and systems',
            today: 'The company’s website is its Instagram profile.',
            after: 'A site of your own, that shows up on Google.',
          },
          {
            today: 'Changing a price on the site means calling someone.',
            after: 'You change it yourself, in two minutes.',
          },
          {
            today: 'Clients ask for everything over WhatsApp and someone writes it down.',
            after: 'A portal with login where they track it themselves.',
          },
        ],
      },
      industriesOverline: 'Industries',
      industriesTitle: 'Sectors we design automation for',
      industries: [
        'Food and beverage', 'Automotive', 'Pharmaceutical', 'Chemical', 'Metallurgy',
        'Plastics', 'Packaging', 'Pulp and paper', 'Logistics', 'Utilities and sanitation',
      ],
      industriesNote: 'Websites and web systems we build for companies of any size and sector.',
      cta: {
        title: 'Need any of these?',
        body: 'Tell us your case and get a written proposal.',
        button: 'Talk to us',
      },
    },
    // NOTE: prices below are PLACEHOLDERS. Replace with the real ones before publishing.

    process: {
      overline: 'Process',
      title: 'How a project runs',
      intro: 'From first contact to support.',
      steps: [
        { n: '01', title: 'Assessment', desc: 'We understand what you do today and where it hurts.' },
        { n: '02', title: 'Proposal', desc: 'Scope, timeline and price in writing.' },
        { n: '03', title: 'Design', desc: 'Architecture, data and screen prototypes.' },
        { n: '04', title: 'Development', desc: 'Partial deliveries — you follow the progress.' },
        { n: '05', title: 'Validation', desc: 'Testing with real data before it goes live.' },
        { n: '06', title: 'Training', desc: 'We teach whoever will use it.' },
        { n: '07', title: 'Support', desc: 'Fine-tuning and remote assistance.' },
      ],
    },
    faq: {
      overline: 'FAQ',
      title: 'Frequently asked questions',
      items: [
        { q: 'How much does a project cost?', a: 'It depends on the scope. You get a written proposal, no commitment.' },
        { q: 'What is the average timeline?', a: '2 to 6 weeks for simple projects. Larger ones are split into phases, with partial deliveries.' },
        { q: 'Do you serve clients outside São Paulo?', a: 'Yes — since our work is software, we serve all of Brazil fully remotely.' },
        { q: 'Do you build websites too?', a: 'We do: company websites, online stores, landing pages and web systems with login. That side is not industry-only — we serve companies of any size and sector.' },
        { q: 'Do you only handle the software side?', a: 'Yes — we program websites, systems and automations; we do not perform electrical installations or machine retrofits.' },
        { q: 'Do you take over systems built by third parties?', a: 'Yes. We take over, document and improve what already exists.' },
        { q: 'How do I get started?', a: 'Through the form or WhatsApp. Then we set up a call.' },
      ],
    },
    contact: {
      overline: 'Contact',
      title: 'Tell us what you need',
      intro: 'We reply with an initial analysis, no commitment.',
      form: {
        name: 'Name',
        namePh: 'Your full name',
        email: 'Email',
        emailPh: 'you@company.com',
        whatsapp: 'WhatsApp',
        whatsappPh: '+55 11 90000-0000',
        company: 'Company (optional)',
        companyPh: 'Company name',
        message: 'What you need',
        messagePh: 'E.g.: inventory in a spreadsheet, reports built by hand every week, or a website the company still does not have…',
        submit: 'Send',
        sending: 'Sending…',
        successTitle: 'Received.',
        successMsg: 'We will get back to you on the WhatsApp or email you gave us.',
        errorMsg: 'Could not send. Try again or reach us on WhatsApp.',
        required: 'Please fill in the required fields.',
      },
      channelsTitle: 'Direct channels',
      whatsappLabel: 'WhatsApp',
      whatsappValue: '+55 11 99684-5494',
      emailLabel: 'Email',
      emailValue: 'jnautomacoes@gmail.com',
      locationLabel: 'Service',
      locationValue: 'Home office — fully remote, anywhere in Brazil',
      soon: 'Instagram coming soon.',
    },
    footer: {
      tagline: 'Websites, systems and automation with engineering precision.',
      navTitle: 'Navigation',
      contactTitle: 'Contact',
      rights: '© 2026 JN Automações. All rights reserved.',
    },
    notFound: {
      code: '404',
      title: 'Page not found.',
      msg: 'This address does not exist here.',
      back: 'Back to home',
    },
    whatsappMessage: 'Hello! I would like to talk about a project.',
  },
};

const LanguageContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('jn-lang') || 'pt');

  useEffect(() => {
    localStorage.setItem('jn-lang', lang);
    document.documentElement.lang = lang === 'pt' ? 'pt-BR' : 'en';
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
